import React, { SyntheticEvent, useState } from 'react';
import { Pagination } from '@mui/material';
import { Location, PaginationMeta, PaginationOrder } from '@/common/types';
import { a11yProps } from '@/common/utils';
import { filterProvidersByLocationId } from '@/common/utils';
import { PatientTableTabs, PatientTableTab } from '@/components/PatientBilling/PatientsBillingTable/styles';
import { PaginationWrapper } from '@/components/common/Table/styles';
import { usePractitionersContext } from '../Practitioners/context/hooks/PractitionerContext';
import { OrganizationListWrapper } from '../styles';
import { EmptyLocations } from './EmptyLocations';
import { EmptyUserLocations } from './EmptyUserLocations';
import { LocationRow } from './LocationRow';
import { LocationsListHeader } from './LocationsListHeader';
import { LocationListTabsContainer } from './styles';

interface Props {
    locations?: Location[];
    userLocations?: Location[];
    showTabs: boolean;
    allPage: number;
    allOrder: PaginationOrder;
    allMetadata?: PaginationMeta;
    onAllPageChange: (page: number) => void;
    onToggleAllOrder: () => void;
    userPage: number;
    userOrder: PaginationOrder;
    userMetadata?: PaginationMeta;
    onUserPageChange: (page: number) => void;
    onToggleUserOrder: () => void;
}

export const LocationsList = (props: Props) => {
    const {
        locations,
        userLocations,
        showTabs,
        allPage,
        allOrder,
        allMetadata,
        onAllPageChange,
        onToggleAllOrder,
        userPage,
        userOrder,
        userMetadata,
        onUserPageChange,
        onToggleUserOrder,
    } = props;
    const [currentTab, setCurrentTab] = useState(0);
    const { practitioners } = usePractitionersContext();

    const isAllTab = currentTab === 0;
    const locationArray = isAllTab ? locations : userLocations;
    const empty = isAllTab ? <EmptyLocations /> : <EmptyUserLocations />;
    const activeOrder = isAllTab ? allOrder : userOrder;
    const activePage = isAllTab ? allPage : userPage;
    const activeTotalPages = (isAllTab ? allMetadata?.totalPages : userMetadata?.totalPages) ?? 0;

    const locationRows =
        locationArray &&
        locationArray.map((location) => {
            const filteredPractitioners = filterProvidersByLocationId(location.id!, practitioners);
            return <LocationRow key={location.id} location={location} practitioners={filteredPractitioners} />;
        });

    const handleTabChange = (_: SyntheticEvent, newValue: number) => {
        setCurrentTab(newValue);
    };

    const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
        if (isAllTab) {
            onAllPageChange(value);
        } else {
            onUserPageChange(value);
        }
    };

    const handleToggleOrder = () => {
        if (isAllTab) {
            onToggleAllOrder();
        } else {
            onToggleUserOrder();
        }
    };

    const showPagination = activeTotalPages > 1;

    return (
        <>
            <LocationListTabsContainer>
                {showTabs && (
                    <PatientTableTabs value={currentTab} onChange={handleTabChange}>
                        <PatientTableTab label="All Locations" {...a11yProps(0)} />
                        <PatientTableTab label="My Locations" {...a11yProps(1)} />
                    </PatientTableTabs>
                )}
            </LocationListTabsContainer>
            <OrganizationListWrapper>
                <LocationsListHeader showSort order={activeOrder} onToggleOrder={handleToggleOrder} />
                <>{locationRows ? (locationRows.length > 0 ? locationRows : empty) : null}</>
            </OrganizationListWrapper>
            {showPagination && (
                <PaginationWrapper>
                    <Pagination
                        count={activeTotalPages}
                        page={activePage}
                        color="primary"
                        variant="outlined"
                        shape="rounded"
                        onChange={handlePageChange}
                    />
                </PaginationWrapper>
            )}
        </>
    );
};
